import { NAV_MENU_TYPE } from '@eureka/shared/nav-bar';
import { E_ShareType } from '@eureka/services/src/types/share';
import { AuthService } from '@eureka/shared/share-tools/logic';
import { addTrialDeferred, isUserLogin } from '@eureka/utils/account';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { RouterView, createRouter, createWebHashHistory } from 'vue-router';
import { proxyRouterList } from './proxy-router';
import { deprecatedRouter } from './deprecated-router';
import { HomeService } from '@/domains/home/services/home-service';
import { controlDeferred } from '@eureka/utils/control';
import { VITE_BASE_URL } from '@/constants/env';
import { isCNRegion } from '@eureka/utils/environment';

const router = createRouter({
    history: createWebHashHistory(VITE_BASE_URL),
    routes: [
        {
            path: '/',
            component: () => import('@/pages/Layout.vue'),
            redirect: '/home',
            children: [
                deprecatedRouter,
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/home/LSHome.vue'),
                    beforeEnter: async(to, _from, next) => {
                        const isCN = isCNRegion();
                        const isGuest = !await isUserLogin();

                        if (isGuest) {
                            // 未登录状态：中国地区跳转到 enterprise，其他地区停留在 home
                            if (isCN) {
                                next({ path: '/enterprise', query: to.query });
                            } else {
                                next();
                            }
                            return;
                        }

                        // 已登录用户：等待试用权限检查
                        try {
                            await addTrialDeferred.promise;
                            const { isLsPlg } = await controlDeferred.promise;
                            if (isLsPlg) {
                                next();
                            } else {
                                next({ path: '/enterprise', query: to.query });
                            }
                        } catch (error) {
                            next();
                        }
                    },
                },
                {
                    path: '/enterprise',
                    name: 'enterprise',
                    component: () => import('@/pages/home/LS.vue'),
                    beforeEnter: async(to, _from, next) => {
                        const isCN = isCNRegion();
                        const isGuest = !await isUserLogin();

                        if (isGuest) {
                            // 未登录状态：中国地区停留在 enterprise，其他地区跳转到 home
                            if (isCN) {
                                next();
                            } else {
                                next({ path: '/home', query: to.query });
                            }
                            return;
                        }

                        // 已登录用户：等待试用权限检查
                        try {
                            await addTrialDeferred.promise;
                            const { isLsPlg } = await controlDeferred.promise;
                            if (isLsPlg) {
                                next({ path: '/home', query: to.query });
                            } else {
                                next();
                            }
                        } catch (error) {
                            next();
                        }
                    },
                },
                {
                    path: '/explore',
                    name: 'explore',
                    component: () => import('@/pages/ai/Explore.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CUSTOM_AGENT,
                    },
                },
                {
                    path: '/user-profile',
                    name: 'user-profile',
                    component: () => import('@/pages/UserProfile.vue'),
                },
                {
                    name: 'ai-ls',
                    path: '/ai-ls',
                    children: [
                        {
                            path: ':lsAgentName',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-entry',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lsAgentName/:lsAgentId',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-detail',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lifesciences*',
                            name: 'ai-ls-page',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        }
                    ],
                },
                {
                    name: 'userSetting',
                    path: '/user-setting',
                    component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Layout.vue'),
                    children: [
                        {
                            path: 'amount',
                            name: 'userSettingAmount',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Amount.vue'),
                        },
                        {
                            path: 'package',
                            name: 'userSettingPackage',
                            component: RouterView,
                            children: [
                                {
                                    path: 'ls',
                                    name: 'userSettingPackageLS',
                                    component: () => import(/* webpackChunkName: "air-usersetting-LS" */'@/pages/user-setting/new-version/product-lines/LS.vue'),
                                },
                                {
                                    path: 'ls/plg',
                                    name: 'userSettingPackageLS_PLG',
                                    component: () => import(/* webpackChunkName: "air-usersetting-LSPlg" */'@/pages/user-setting/new-version/product-lines/LsPlg.vue'),
                                }
                            ],
                        },
                        {
                            path: 'agent-settings',
                            name: 'userSettingAgentSettings',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/AgentSettings.vue'),
                        }
                    ],
                },
                {
                    name: 'graph',
                    path: '/graph',
                    meta: {
                        navModuleType: NAV_MENU_TYPE.WORK_PROJECT,
                    },
                    children: [
                        {
                            path: '',
                            name: 'GraphHome',
                            component: () => import('@/pages/graph/index.vue'),
                        },
                        {
                            path: ':graph_id',
                            name: 'GraphDetail',
                            component: () => import('@/pages/graph/detail.vue'),
                            beforeEnter: async(to, from, next) => {
                                const authService = injectService<AuthService>(AuthService);
                                await authService.shareAccessCheck({
                                    data_id: to.params.graph_id as string,
                                    data_type: E_ShareType.PROJECT,
                                });
                                next();
                            },
                        },
                        {
                            path: ':graph_id/report',
                            name: 'GraphReport',
                            component: () => import('@/pages/graph/report.vue'),
                        },
                        {
                            path: ':graph_id/pdf/:item_id',
                            name: 'GraphPdf',
                            component: () => import('@/pages/graph/pdf.vue'),
                        }
                    ],
                },
                {
                    path: '/403',
                    component: () => import('@/pages/403/Unauthorized.vue'),
                },
                {
                    path: '/404',
                    component: () => import('@/pages/404/NotFound.vue'),
                }
            ],
        },
        {
            name: 'onboarding',
            path: '/onboarding',
            component: () => import('@/pages/Onboarding.vue'),
        },
        {
            path: '/ai',
            name: 'ai',
            redirect: '/home',
            component: () => import('@/pages/Layout.vue'),
            meta: {
                navModuleType: NAV_MENU_TYPE.AI_GENERATE,
            },
            children: [
                {
                    path: '/ai/agent',
                    name: 'ai-agent',
                    redirect: '/home',
                },
                {
                    path: '/ai/agent-flow/:agent_job_id',
                    name: 'ai-agent-flow',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.agent_job_id as string,
                            data_type: E_ShareType.AI_AGENT,
                        });
                        next();
                    },
                    component: () => import('@/pages/ai/AgentFlow.vue'),
                }
            ],
        },
        ...proxyRouterList,
        // 通配符路由 - 捕获所有未匹配的路径并重定向到404页面
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            redirect: '/404',
        }
    ],
});

router.beforeEach(async(to, _from, next) => {
    // 全局路由守卫（如需添加全局逻辑，可在此处添加）
    // const isGuest = !await isUserLogin();
    const isCN = isCNRegion();
    switch (to.name) {
    case 'userSettingPackage': {
        // 只有当访问 userSettingPackage 本身时，才进行重定向
        let homepageType = isCN ? 'LS' : 'LS_PLG';
        try {
            const homeService = injectService<HomeService>(HomeService);
            const data = await homeService.getHomePageType();
            homepageType = data.homepage_type;
        } catch {
        }

        switch (homepageType) {
        case 'RD':
        case 'IP':
        case 'MSE':
        case 'LS': {
            return next({
                name: 'userSettingPackageLS',
                query: to.query,
            })
        }
        case 'LS_PLG':
            return next({
                name: isCN ? 'userSettingPackageLS' : 'userSettingPackageLS_PLG',
                query: to.query,
            })
        default:
            break;
        }
        break;
    }
    default:{
        break;
    }
    }
    next();
});

export default router;
